/**
 * Application Command Center, the College Application execution surface.
 *
 * Design brief from the build program (§5-7):
 *   • Pipeline view: every school as a card with priority tier + readiness + deadline.
 *   • Detail view: every requirement, its state, who owns it, and the single next best action.
 *   • Zero AI on this page: readiness, priorities and next-action are computed backend-side.
 *
 * Two views on one route:
 *   /app/applications             → pipeline (list of cards)
 *   /app/applications/:appId      → detail
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AppLayout } from "../components/Layout";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, CheckCircle2, Circle, Clock, ExternalLink,
  FileText, GraduationCap, Loader2, Send, ShieldCheck, TrendingUp,
  UserCheck, AlertTriangle, Sparkles, Target, PlusCircle,
  Shield, Zap, HelpCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Partnership status colors ────────────────────────────────────────────
const PARTNERSHIP_STATUS = {
  live:         { label: "Live",                       cls: "bg-emerald-500/20 text-emerald-200 ring-emerald-400/40" },
  contracted:   { label: "Contracted",                 cls: "bg-cyan-500/20 text-cyan-200 ring-cyan-400/40" },
  approved:     { label: "Approved · integrating",     cls: "bg-sky-500/20 text-sky-200 ring-sky-400/40" },
  in_review:    { label: "In review",                  cls: "bg-amber-500/20 text-amber-200 ring-amber-400/40" },
  contacted:    { label: "Contacted",                  cls: "bg-amber-500/20 text-amber-200 ring-amber-400/40" },
  discovery:    { label: "Partnership discovery",      cls: "bg-fuchsia-500/20 text-fuchsia-200 ring-fuchsia-400/40" },
  not_started:  { label: "Not initiated",              cls: "bg-slate-700 text-slate-300 ring-slate-600" },
};

function PartnershipBanner({ integrations }) {
  if (!integrations) return null;
  const featured = ["common_app", "parchment", "coalition"];
  return (
    <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-4 space-y-3"
         data-testid="partnership-banner">
      <div className="flex items-start gap-2">
        <Shield className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide text-cyan-300 font-semibold">
            Integration Partnership Status
          </div>
          <div className="text-sm text-slate-300 mt-0.5">
            Every workflow works today. When each partnership is approved, the
            manual step becomes automatic, <span className="text-slate-100 font-semibold">zero code change</span>.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {featured.map(key => {
          const i = integrations[key];
          if (!i) return null;
          const meta = PARTNERSHIP_STATUS[i.partnership_status] || PARTNERSHIP_STATUS.not_started;
          return (
            <div key={key} className="rounded-lg bg-slate-800/60 ring-1 ring-slate-700 px-3 py-2"
                 data-testid={`integration-${key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-white truncate">{i.provider}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${meta.cls}`}>
                  {meta.label}
                </span>
              </div>
              <div className="text-[11px] text-slate-300 mt-1 line-clamp-2">
                {i.phase_a_available
                  ? <>✓ <span className="text-slate-300">Phase A live:</span> {i.phase_a_description}</>
                  : <>⏳ {i.partnership_notes}</>
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Design tokens (kept local; deliberately bright + demo-friendly) ───────
const TIER_COLORS = {
  reach:  { bg: "bg-fuchsia-500/15", ring: "ring-fuchsia-400/40", text: "text-fuchsia-200", dot: "bg-fuchsia-400" },
  target: { bg: "bg-sky-500/15",     ring: "ring-sky-400/40",     text: "text-sky-200",     dot: "bg-sky-400" },
  safety: { bg: "bg-emerald-500/15", ring: "ring-emerald-400/40", text: "text-emerald-200", dot: "bg-emerald-400" },
  null:   { bg: "bg-slate-500/15",   ring: "ring-slate-400/30",   text: "text-slate-300",   dot: "bg-slate-400" },
};
const STATE_META = {
  interested:        { label: "Interested",        chip: "bg-slate-700 text-slate-300" },
  planning:          { label: "Planning",          chip: "bg-slate-700 text-slate-300" },
  started:           { label: "Started",           chip: "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40" },
  docs_needed:       { label: "Docs Needed",       chip: "bg-orange-500/20 text-orange-200 ring-1 ring-orange-400/40" },
  ready_to_submit:   { label: "Ready to Submit",   chip: "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40" },
  submitted:         { label: "Submitted",         chip: "bg-cyan-500/20 text-cyan-200 ring-1 ring-cyan-400/40" },
  awaiting_decision: { label: "Awaiting Decision", chip: "bg-violet-500/20 text-violet-200 ring-1 ring-violet-400/40" },
  accepted:          { label: "Accepted",          chip: "bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-300/60" },
  waitlisted:        { label: "Waitlisted",        chip: "bg-yellow-500/20 text-yellow-200 ring-1 ring-yellow-400/40" },
  denied:            { label: "Denied",            chip: "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/40" },
  enrolled:          { label: "Enrolled",          chip: "bg-fuchsia-500/25 text-fuchsia-100 ring-1 ring-fuchsia-300/60" },
};
const REQ_STATE_META = {
  not_started:    { label: "Not Started",    icon: Circle,          color: "text-slate-300" },
  in_progress:    { label: "In Progress",    icon: Loader2,         color: "text-amber-300" },
  requested:      { label: "Requested",      icon: Send,            color: "text-sky-300" },
  submitted:      { label: "Submitted",      icon: CheckCircle2,    color: "text-cyan-300" },
  complete:       { label: "Complete",       icon: CheckCircle2,    color: "text-emerald-300" },
  waived:         { label: "Waived",         icon: ShieldCheck,     color: "text-slate-300" },
  not_applicable: { label: "N/A",            icon: Circle,          color: "text-slate-300" },
  blocked:        { label: "Blocked",        icon: AlertTriangle,   color: "text-rose-300" },
};

function ReadinessBar({ pct }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const hue = clamped >= 100 ? "from-emerald-400 to-emerald-500"
            : clamped >= 60  ? "from-sky-400 to-sky-500"
            : clamped >= 30  ? "from-amber-400 to-orange-400"
            :                  "from-slate-500 to-slate-400";
  return (
    <div className="w-full h-2 bg-slate-800/70 rounded-full overflow-hidden">
      <div className={`h-full bg-gradient-to-r ${hue} transition-all`}
           style={{ width: `${clamped}%` }} />
    </div>
  );
}

function DeadlinePill({ days }) {
  if (days === null || days === undefined) return <span className="text-xs text-slate-300">-</span>;
  const urgent = days <= 14;
  const soon   = days <= 45;
  const cls = urgent ? "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/40"
            : soon   ? "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40"
            :          "bg-slate-700/60 text-slate-300 ring-1 ring-slate-600/60";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
          data-testid="deadline-pill">
      <Clock className="w-3 h-3" />
      {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`}
    </span>
  );
}

function TierChip({ tier }) {
  const t = TIER_COLORS[tier] || TIER_COLORS.null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${t.bg} ${t.ring} ${t.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
      {tier ? tier.toUpperCase() : "UNRANKED"}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PIPELINE VIEW
// ═══════════════════════════════════════════════════════════════════════════
function PipelineView({ dashboard, applications, onOpenApp, onAddCollege, integrations }) {
  const totals = dashboard?.totals || {};
  const avg    = dashboard?.average_readiness_pct || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            College Applications
          </h1>
        </div>
        <button
          data-testid="add-college-btn"
          onClick={onAddCollege}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-semibold hover:from-sky-400 hover:to-cyan-400 transition-all shadow-lg shadow-sky-500/25"
        >
          <PlusCircle className="w-4 h-4" />
          Add a College
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Applications" value={totals.applications || 0} icon={GraduationCap} accent="sky" />
        <SummaryCard label="In Progress"  value={totals.in_flight || 0}    icon={TrendingUp}    accent="amber" />
        <SummaryCard label="Submitted"    value={totals.submitted || 0}    icon={Send}          accent="cyan" />
        <SummaryCard label="Accepted"     value={totals.accepted || 0}     icon={CheckCircle2}  accent="emerald" />
        <SummaryCard label="Avg Readiness" value={`${avg}%`}               icon={ShieldCheck}   accent="violet" />
      </div>

      {/* Applications grid */}
      {applications.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 p-12 text-center">
          <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <div className="text-lg font-semibold text-slate-200">No applications yet</div>
          <div className="text-sm text-slate-300 mt-1 mb-4">
            Add a college from your saved list or search for one to get started.
          </div>
          <button
            onClick={onAddCollege}
            data-testid="empty-add-college-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 text-white font-semibold hover:bg-sky-400"
          >
            <PlusCircle className="w-4 h-4" /> Add your first college
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {applications.map((app) => (
            <ApplicationCard key={app.id} app={app} onOpen={() => onOpenApp(app.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, accent }) {
  const rings = {
    sky:     "ring-sky-500/30 bg-sky-500/10 text-sky-200",
    amber:   "ring-amber-500/30 bg-amber-500/10 text-amber-200",
    cyan:    "ring-cyan-500/30 bg-cyan-500/10 text-cyan-200",
    emerald: "ring-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    violet:  "ring-violet-500/30 bg-violet-500/10 text-violet-200",
  };
  return (
    <div className={`rounded-xl ring-1 backdrop-blur px-4 py-3 ${rings[accent]}`}
         data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="w-4 h-4 opacity-80" />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function ApplicationCard({ app, onOpen }) {
  const tier = TIER_COLORS[app.destination_priority_tier] || TIER_COLORS.null;
  const stateMeta = STATE_META[app.state] || STATE_META.planning;
  return (
    <button
      onClick={onOpen}
      data-testid={`app-card-${app.id}`}
      className={`text-left rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/40 hover:from-slate-800/80 hover:to-slate-900/60 hover:border-slate-700 transition-all shadow-lg shadow-black/20 overflow-hidden`}
    >
      {/* Top strip */}
      <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <TierChip tier={app.destination_priority_tier} />
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stateMeta.chip}`}>
              {stateMeta.label}
            </span>
            <span className="text-xs text-slate-300 uppercase tracking-wide">
              {(app.submission_channel || "").replace("_", " ")}
            </span>
          </div>
          <h3 className="text-lg font-bold text-white mt-2 truncate">{app.destination_name}</h3>
          <div className="text-xs text-slate-300 mt-0.5">
            {app.application_type && app.application_type !== "not_set"
              ? app.application_type.replace("_", " ").toUpperCase()
              : "Application type not set"}
          </div>
        </div>
        <DeadlinePill days={app.days_until_deadline} />
      </div>

      {/* Fit scores strip */}
      {app.destination_fit_scores && Object.keys(app.destination_fit_scores).length > 0 && (
        <div className="px-5 py-2 grid grid-cols-4 gap-2 border-t border-slate-800 bg-slate-900/40">
          <FitPill label="FuturePath Fit" value={app.destination_fit_scores.futurepath_fit} color="sky" />
          <FitPill label="Admissions Fit" value={app.destination_fit_scores.admissions_fit} color="fuchsia" />
          <FitPill label="Financial Fit"  value={app.destination_fit_scores.financial_fit}  color="emerald" />
          <FitPill label="Career ROI"     value={app.destination_fit_scores.career_roi}     color="amber" />
        </div>
      )}

      {/* Readiness bar */}
      <div className="px-5 py-3 border-t border-slate-800">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-slate-300 font-medium">Readiness</span>
          <span className="text-slate-200 font-bold">
            {app.readiness_pct}% ({app.completed_requirement_count}/{app.total_requirement_count})
          </span>
        </div>
        <ReadinessBar pct={app.readiness_pct} />
      </div>

      {/* Next best action */}
      <div className="px-5 pb-4 pt-2 flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
        <div>
          <div className="text-[11px] uppercase tracking-wide text-cyan-300 font-semibold">Next Best Action</div>
          <div className="text-sm text-slate-100 font-medium leading-snug">
            {app.next_best_action || "You're all set, nothing to do."}
          </div>
        </div>
      </div>
    </button>
  );
}

function FitPill({ label, value, color }) {
  if (value === undefined || value === null) return <div />;
  const map = {
    sky: "text-sky-200", fuchsia: "text-fuchsia-200",
    emerald: "text-emerald-200", amber: "text-amber-200",
  };
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-300 font-semibold">{label}</div>
      <div className={`text-sm font-bold ${map[color]}`}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DETAIL VIEW, /app/applications/:appId
// ═══════════════════════════════════════════════════════════════════════════
function DetailView({ appId, onBack, refreshDashboard }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/transitions/applications/${appId}`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load application", { description: e.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [appId]);

  const patchRequirement = async (reqId, state) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/transitions/requirements/${reqId}`, { state }, getAuthHeader());
      await load();
      refreshDashboard?.();
      toast.success(`Marked ${REQ_STATE_META[state]?.label || state}`);
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const handoff = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/transitions/applications/${appId}/handoff`, {}, getAuthHeader());
      window.open(r.data.handoff_url, "_blank", "noopener");
      toast.success("Opened in a new tab", {
        description: "When you finish there, come back and click 'I submitted this'.",
      });
      await load();
      refreshDashboard?.();
    } catch (e) {
      toast.error("Handoff failed", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const confirmSubmitted = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/transitions/applications/${appId}/confirm-submitted`,
                       {}, getAuthHeader());
      await load();
      refreshDashboard?.();
      toast.success("Marked submitted, nice work.");
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const { application: app, requirements, requests, destination } = data;
  const nextReqId = app.next_best_action_requirement_id;
  const tier = TIER_COLORS[destination?.priority_tier] || TIER_COLORS.null;
  const stateMeta = STATE_META[app.state] || STATE_META.planning;

  return (
    <div className="space-y-6">
      <button onClick={onBack} data-testid="back-to-pipeline"
              className="inline-flex items-center gap-2 text-slate-300 hover:text-slate-200 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to pipeline
      </button>

      {/* Header card */}
      <div className={`rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/30 p-6 ${tier.ring} ring-1`}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <TierChip tier={destination?.priority_tier} />
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stateMeta.chip}`}>
                {stateMeta.label}
              </span>
              <DeadlinePill days={app.deadline ? Math.floor((new Date(app.deadline) - new Date()) / 86400000) : null} />
            </div>
            <h1 className="text-3xl font-bold text-white mt-3">{destination?.name}</h1>
            <div className="text-slate-300 mt-1">
              Applying via <span className="text-slate-200 font-semibold">
                {(app.submission_channel || "manual").replace("_", " ")}
              </span>
              {app.application_type && app.application_type !== "not_set"
                ? <> · <span className="text-slate-200">{app.application_type.replace("_", " ")}</span></>
                : null}
            </div>
          </div>

          {/* Readiness dial */}
          <div className="w-full md:w-56 shrink-0">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">Readiness</span>
              <span className="text-2xl font-bold text-white">{app.readiness_pct}%</span>
            </div>
            <ReadinessBar pct={app.readiness_pct} />
            <div className="text-xs text-slate-300 mt-1.5">
              {app.completed_requirement_count} of {app.total_requirement_count} complete
            </div>
          </div>
        </div>

        {/* Next best action banner */}
        {app.next_best_action && (
          <div className="mt-5 rounded-xl bg-gradient-to-r from-cyan-500/10 to-sky-500/10 ring-1 ring-cyan-400/30 px-4 py-3 flex items-start gap-3"
               data-testid="next-best-action-banner">
            <Sparkles className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wide text-cyan-300 font-semibold">Next Best Action</div>
              <div className="text-white font-semibold">{app.next_best_action}</div>
            </div>
            {nextReqId && (() => {
              const nextReq = requirements.find(r => r.id === nextReqId);
              if (!nextReq) return null;
              if (nextReq.kind === "external_channel_link") {
                return (
                  <button onClick={handoff} disabled={busy}
                          data-testid="handoff-btn-inline"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold shadow-sm">
                    <ExternalLink className="w-3.5 h-3.5" /> Continue
                  </button>
                );
              }
              if (["counselor_rec", "teacher_rec", "transcript", "school_report"].includes(nextReq.kind)) {
                return (
                  <button onClick={() => patchRequirement(nextReq.id, "requested")}
                          disabled={busy}
                          data-testid="request-btn-inline"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold shadow-sm">
                    <Send className="w-3.5 h-3.5" /> Request
                  </button>
                );
              }
              return (
                <button onClick={() => patchRequirement(nextReq.id, "complete")}
                        disabled={busy}
                        data-testid="complete-btn-inline"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold shadow-sm">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Mark done
                </button>
              );
            })()}
          </div>
        )}

        {/* Big handoff / submit buttons for terminal-ish states */}
        <div className="mt-5 flex flex-wrap gap-2">
          {app.readiness_pct >= 100 && app.state !== "submitted" && app.state !== "enrolled" && (
            <button onClick={confirmSubmitted} disabled={busy}
                    data-testid="mark-submitted-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold shadow-lg shadow-emerald-500/30">
              <CheckCircle2 className="w-4 h-4" /> I already submitted this
            </button>
          )}
          {app.submission_channel && app.submission_state !== "submitted_on_channel" && (
            <button onClick={handoff} disabled={busy}
                    data-testid="handoff-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold ring-1 ring-white/20">
              <ExternalLink className="w-4 h-4" /> Continue on Common App
            </button>
          )}
        </div>
      </div>

      {/* Requirements list */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-sky-300" />
            Requirements ({requirements.length})
          </h2>
          <span className="text-xs text-slate-300">
            Higher priority items shown first
          </span>
        </div>
        <ul className="divide-y divide-slate-800">
          {requirements.map((r) => (
            <RequirementRow key={r.id} req={r}
                            isNext={r.id === nextReqId}
                            onPatch={(state) => patchRequirement(r.id, state)}
                            busy={busy} />
          ))}
        </ul>
      </div>

      {/* Requests */}
      {requests.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-fuchsia-300" />
              Who owes what ({requests.length})
            </h2>
          </div>
          <ul className="divide-y divide-slate-800">
            {requests.map((rq) => (
              <li key={rq.id} className="px-5 py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="text-white font-medium">
                    {rq.recipient_name}
                    <span className="text-xs text-slate-300 ml-2">({rq.recipient_role})</span>
                  </div>
                  {rq.message && <div className="text-xs text-slate-300 mt-0.5 line-clamp-1">{rq.message}</div>}
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/30 uppercase tracking-wide">
                  {rq.state}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RequirementRow({ req, isNext, onPatch, busy }) {
  const meta = REQ_STATE_META[req.state] || REQ_STATE_META.not_started;
  const Icon = meta.icon;
  const isTerminal = ["complete", "waived", "not_applicable"].includes(req.state);

  return (
    <li className={`px-5 py-3.5 flex items-center gap-4 ${isNext ? "bg-cyan-500/5" : ""}`}
        data-testid={`req-row-${req.kind}`}>
      <Icon className={`w-5 h-5 shrink-0 ${meta.color} ${req.state === "in_progress" ? "animate-spin" : ""}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-slate-100 font-medium truncate">{req.label}</span>
          {isNext && (
            <span className="text-[10px] uppercase tracking-wide bg-cyan-500/20 text-cyan-200 px-1.5 py-0.5 rounded-full font-bold ring-1 ring-cyan-400/40">
              Next
            </span>
          )}
        </div>
        <div className="text-xs text-slate-300 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="uppercase">{meta.label}</span>
          <span>·</span>
          <span className="capitalize">Owner: {req.owner_role || "student"}</span>
          {req.provider && req.provider !== "manual" && (
            <>
              <span>·</span>
              <span className="capitalize">via {req.provider.replace("_", " ")}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {!isTerminal && (
          <>
            {["counselor_rec", "teacher_rec", "transcript", "school_report"].includes(req.kind) &&
             req.state === "not_started" && (
              <button onClick={() => onPatch("requested")} disabled={busy}
                      data-testid={`req-request-${req.kind}`}
                      className="text-xs px-2.5 py-1 rounded-md bg-sky-500/15 hover:bg-sky-500/25 text-sky-200 ring-1 ring-sky-400/30">
                Request
              </button>
            )}
            <button onClick={() => onPatch("complete")} disabled={busy}
                    data-testid={`req-complete-${req.kind}`}
                    className="text-xs px-2.5 py-1 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 ring-1 ring-emerald-400/30">
              Mark done
            </button>
          </>
        )}
        {isTerminal && req.state === "complete" && (
          <span className="text-xs text-emerald-300 font-semibold">✓ Done</span>
        )}
      </div>
    </li>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// "Add a college" modal
// ═══════════════════════════════════════════════════════════════════════════
function AddCollegeModal({ open, onClose, onCreated }) {
  const { getAuthHeader } = useAuth();
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("common_app");
  const [type, setType] = useState("regular");
  const [deadline, setDeadline] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        college_name: name.trim(),
        submission_channel: channel,
        application_type: type,
        deadline: deadline || undefined,
      };
      const r = await axios.post(`${API}/transitions/applications`, payload, getAuthHeader());
      toast.success(`Added ${name}`);
      onCreated?.(r.data);
      setName(""); setDeadline("");
      onClose();
    } catch (err) {
      toast.error("Couldn't add", { description: err.response?.data?.detail });
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
         onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 space-y-4"
            data-testid="add-college-modal">
        <div>
          <h3 className="text-lg font-bold text-white">Add a college</h3>
          <p className="text-xs text-slate-300 mt-1">
            We'll create the application with the standard requirement checklist.
          </p>
        </div>

        <label className="block">
          <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">College name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required
                 data-testid="add-college-name"
                 placeholder="UNC Chapel Hill"
                 className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 text-white ring-1 ring-slate-700 focus:ring-sky-400 focus:outline-none" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">Apply via</span>
            <select value={channel} onChange={(e) => setChannel(e.target.value)}
                    data-testid="add-college-channel"
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 text-white ring-1 ring-slate-700">
              <option value="common_app">Common App</option>
              <option value="coalition">Coalition</option>
              <option value="direct_portal">Direct portal</option>
              <option value="manual">Manual / other</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="add-college-type"
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 text-white ring-1 ring-slate-700">
              <option value="regular">Regular</option>
              <option value="early_action">Early Action</option>
              <option value="early_decision">Early Decision</option>
              <option value="rolling">Rolling</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">
            Application deadline
          </span>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                 data-testid="add-college-deadline"
                 className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 text-white ring-1 ring-slate-700" />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-800">
            Cancel
          </button>
          <button type="submit" disabled={busy || !name.trim()}
                  data-testid="add-college-submit"
                  className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-semibold disabled:opacity-50">
            {busy ? "Adding..." : "Add application"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE ROOT
// ═══════════════════════════════════════════════════════════════════════════
export default function ApplicationCommandCenterPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const { appId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [applications, setApplications] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const loadPipeline = async () => {
    try {
      const [d, apps, integ] = await Promise.all([
        axios.get(`${API}/transitions/dashboard`, getAuthHeader()),
        axios.get(`${API}/transitions/applications`, getAuthHeader()),
        axios.get(`${API}/transitions/integrations/status`, getAuthHeader()),
      ]);
      setDashboard(d.data);
      setIntegrations(integ.data.integrations);
      // Merge dashboard upcoming (has days_until_deadline) into applications
      const upcomingById = Object.fromEntries((d.data.upcoming || []).map(u => [u.application_id, u]));
      const merged = (apps.data.applications || []).map(a => ({
        ...a,
        days_until_deadline: upcomingById[a.id]?.days_until_deadline ?? null,
      }));
      // Sort: readiness_pct descending, then deadline ascending
      merged.sort((a, b) => {
        const aReady = a.state === "ready_to_submit" || a.readiness_pct >= 100 ? 0 : 1;
        const bReady = b.state === "ready_to_submit" || b.readiness_pct >= 100 ? 0 : 1;
        if (aReady !== bReady) return aReady - bReady;
        const aDays = a.days_until_deadline ?? 9999;
        const bDays = b.days_until_deadline ?? 9999;
        return aDays - bDays;
      });
      setApplications(merged);
    } catch (e) {
      toast.error("Couldn't load applications", { description: e.response?.data?.detail });
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPipeline(); }, []);

  return (
    <AppLayout>
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
         data-testid="application-command-center">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-300">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : appId ? (
          <DetailView appId={appId}
                      onBack={() => navigate("/app/applications")}
                      refreshDashboard={loadPipeline} />
        ) : (
          <PipelineView dashboard={dashboard}
                        applications={applications}
                        integrations={integrations}
                        onOpenApp={(id) => navigate(`/app/applications/${id}`)}
                        onAddCollege={() => setShowAdd(true)} />
        )}
      </div>
      <AddCollegeModal open={showAdd} onClose={() => setShowAdd(false)}
                       onCreated={() => loadPipeline()} />
    </div>
    </AppLayout>
  );
}
